'use client';
import React, {
    Dispatch,
    FunctionComponent,
    SetStateAction,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { Box, List, ListItemButton, ListItemText, Typography } from '@mui/material';
import { styled } from '@mui/system';
import { DynamicRequestBody } from '../../../schemas/DynamicRequestBody';
import { ReturnParameterValueInFilter } from '../../../schemas/ReturnParameterValueInFilter';
import { useResponsive } from '../../../contexts/ResponsiveContext';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const ScrollBarContainer = styled(Box)({
    flex: 1,
    overflowY: 'auto',
    // The main scroll area for the item list
    '::-webkit-scrollbar': {
        width: '0.75rem',
    },
    '::-webkit-scrollbar-track': {
        background: '#F2F2F2',
        borderRadius: '10px',
    },
    '::-webkit-scrollbar-thumb': {
        backgroundColor: '#B75332',
        borderRadius: '20px',
        border: '2px solid transparent',
    },
});

const ClearButton = styled(Box)({
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '25px', // from Figma
    backgroundColor: '#B75332',
    color: '#FFFFFF',
    textAlign: 'center',
    fontFamily: 'Poppins',
    fontWeight: 400,
    fontSize: '16px',
    lineHeight: '19px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '0px 0px 10px 10px',
    cursor: 'pointer',
    '&:hover': {
        backgroundColor: '#EE9769',
    },
});

const getDisplayNameForParameterValues = (
    param: ReturnParameterValueInFilter,
    language: string
) => {
    const displayName =
        param.displayNames?.find((d) => d.language === language) ||
        param.displayNames?.find((d) => d.language === 'ENGLISH');

    return displayName?.display || param.formattedValue?.toString();
};

interface FilterComponentProps {
    isFilterActive: boolean;
    title: string;
    originOptions: ReturnParameterValueInFilter[];
    setGlobalFilterState: Dispatch<SetStateAction<DynamicRequestBody | undefined>>;
    filteredOptions: ReturnParameterValueInFilter[] | undefined;
    globalFilterState: DynamicRequestBody | undefined;
    onActivateFilter?: (filterKey: string) => void;
    onDeactivateFilter?: (filterKey: string) => void;
    firstKeyAllowedMultipleSelect?: string | null;
    filterKey: string;
    language: string;
}

const FilterComponent: FunctionComponent<FilterComponentProps> = ({
    isFilterActive,
    title,
    originOptions,
    setGlobalFilterState,
    filteredOptions,
    globalFilterState,
    onActivateFilter,
    onDeactivateFilter,
    firstKeyAllowedMultipleSelect,
    filterKey,
    language,
}) => {
    const safeOptions = Array.isArray(originOptions) ? originOptions : [originOptions];

    /* --- 選取相關 state --- */
    const [selected, setSelected] = useState<number[]>([]);
    const [anchorIdx, setAnchorIdx] = useState<number | null>(null); // shift‑click 錨點
    const [isDragging, setIsDragging] = useState(false);
    const dragStartIdx = useRef<number | null>(null);

    /* ---------- debounced commit ---------- */
    const commitTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

    /** 將「選取」推進到 view；真正 commit 則在 0.5s 後（無操作才觸發） */
    const queueCommit = useCallback(
        (vals: number[]) => {
            // 1. 先讓畫面立刻有 selected 狀態
            setSelected(vals);

            // 2. debounce 寫回 globalFilterState
            if (commitTimeout.current) clearTimeout(commitTimeout.current);
            commitTimeout.current = setTimeout(() => {
                setGlobalFilterState(
                    (prev) => ({ ...(prev || {}), [filterKey]: vals }) as DynamicRequestBody
                );
            }, 500); // 0.5 s
        },
        [setGlobalFilterState, filterKey]
    );

    /** 清理計時器（組件 unmount 或依賴改變時） */
    useEffect(() => {
        return () => {
            if (commitTimeout.current) clearTimeout(commitTimeout.current);
        };
    }, []);

    /* --- 初始／外部變更時更新選取狀態 --- */
    useEffect(() => {
        if (globalFilterState?.[filterKey]) {
            setSelected(globalFilterState[filterKey] as number[]);
        } else setSelected([]);
    }, [globalFilterState, filterKey]);

    /* --- 篩選後啟用/停用清單項目 --- */
    const { filteredSet, isEmptyAfterFilter } = useMemo(() => {
        if (!filteredOptions?.length)
            return { filteredSet: new Set<number>(), isEmptyAfterFilter: true };
        const set = new Set<number>(filteredOptions.map((i) => i.id));
        return { filteredSet: set, isEmptyAfterFilter: set.size === 0 };
    }, [filteredOptions]);

    const isFirstKey = firstKeyAllowedMultipleSelect === filterKey;

    /* ---------- 滑鼠事件核心 ---------- */
    const handleMouseDown = useCallback(
        (e: React.MouseEvent<HTMLDivElement>, idx: number, id: number, disabled: boolean) => {
            if (disabled) return;
            const isCtrl = e.ctrlKey || e.metaKey;
            const isShift = e.shiftKey;

            /* -- 1. Shift + click：與錨點形成區段 -- */
            if (isShift && anchorIdx !== null) {
                const start = Math.min(anchorIdx, idx);
                const end = Math.max(anchorIdx, idx);
                const rangeIds = safeOptions.slice(start, end + 1).map((o) => o.id);
                const next = isCtrl ? Array.from(new Set([...selected, ...rangeIds])) : rangeIds;
                queueCommit(next);
            } else if (isCtrl) {
                /* -- 2. Ctrl/⌘ + click：單獨切換此項 -- */
                queueCommit(
                    selected.includes(id) ? selected.filter((v) => v !== id) : [...selected, id]
                );
                setAnchorIdx(idx);
            } else {
                /* -- 3. 單純 click：只選單一項（若已選中則取消） -- */
                if (selected.length === 1 && selected[0] === id) {
                    // 再點一次 → 取消選取
                    queueCommit([]);
                    setAnchorIdx(null);
                } else {
                    queueCommit([id]);
                    setAnchorIdx(idx);
                }
            }

            /* -- 為拖曳做準備 -- */
            setIsDragging(true);
            dragStartIdx.current = idx;
            window.addEventListener('mouseup', stopDragging);
        },
        [anchorIdx, queueCommit, safeOptions, selected]
    );

    const handleMouseEnter = useCallback(
        (idx: number) => {
            if (!isDragging || dragStartIdx.current === null) return;
            const start = Math.min(dragStartIdx.current, idx);
            const end = Math.max(dragStartIdx.current, idx);
            const rangeIds = safeOptions.slice(start, end + 1).map((o) => o.id);
            queueCommit(rangeIds);
        },
        [queueCommit, isDragging, safeOptions]
    );

    const stopDragging = useCallback(() => {
        setIsDragging(false);
        dragStartIdx.current = null;
        window.removeEventListener('mouseup', stopDragging);
    }, []);

    /* ---------- 清除全部 ---------- */
    const handleClear = useCallback(() => {
        queueCommit([]);
        setAnchorIdx(null);
    }, [queueCommit]);

    /* ---------- RWD ---------- */
    const { isScreenMd, isScreenSm, isScreenXs, responsiveWidth } = useResponsive();
    const isMobile = isScreenMd || isScreenSm || isScreenXs;

    /* ---------- UI ---------- */
    if (!isFilterActive)
        return (
            <Box
                sx={{
                    width: isMobile ? responsiveWidth : '225px',
                    height: '40px',
                    backgroundColor: '#FFFFFF',
                    border: '1px solid #7A7C81',
                    borderRadius: '12px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '10px 5px',
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: '#F2F2F2' },
                }}
                onClick={() => onActivateFilter?.(filterKey)}
                data-testid={`${filterKey}-filter_component`}
            >
                <Typography
                    sx={{
                        fontFamily: 'Poppins',
                        fontWeight: 600,
                        fontSize: 16,
                        lineHeight: '19px',
                        color: '#191A1B',
                    }}
                >
                    {title}
                </Typography>
                {isMobile ? (
                    <ExpandMoreIcon sx={{ width: 18, height: 18, color: 'black' }} />
                ) : (
                    <Typography
                        sx={{
                            fontFamily: 'Poppins',
                            fontWeight: 600,
                            fontSize: 16,
                            lineHeight: '19px',
                        }}
                    >
                        +
                    </Typography>
                )}
            </Box>
        );

    /* ---------- Active Card ---------- */
    return (
        <Box
            sx={{
                position: 'relative',
                width: isMobile ? responsiveWidth : '225px',
                height: '339px',
                backgroundColor: '#FFFFFF',
                border: '1px solid #7A7C81',
                borderRadius: '10px',
                display: 'flex',
                flexDirection: 'column',
            }}
            data-testid={`${filterKey}-filter_component`}
        >
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '10px 5px',
                    borderBottom: '1px solid #7A7C81',
                }}
            >
                <Typography
                    sx={{
                        fontFamily: 'Poppins',
                        fontWeight: 600,
                        fontSize: '16px',
                        lineHeight: '19px',
                        color: '#191A1B',
                    }}
                >
                    {title}
                </Typography>

                {/* close / collapse */}
                {isMobile ? (
                    <Box
                        sx={{ width: 18, height: 18 }}
                        onClick={() => onDeactivateFilter?.(filterKey)}
                    >
                        <ExpandLessIcon sx={{ color: 'black' }} />
                    </Box>
                ) : (
                    <Box
                        sx={{
                            width: 15,
                            height: 15,
                            cursor: 'pointer',
                            position: 'relative',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: '7px',
                                left: 0,
                                right: 0,
                                height: '1px',
                                backgroundColor: '#000',
                                transition: 'opacity 0.3s',
                            },
                            '&:hover::before': { opacity: 0 },
                            '&::after': {
                                content: '"x"',
                                position: 'absolute',
                                inset: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 'bold',
                                fontSize: '14px',
                                color: '#000',
                                opacity: 0,
                                transition: 'opacity 0.3s',
                            },
                            '&:hover::after': { opacity: 1 },
                        }}
                        onClick={() => onDeactivateFilter?.(filterKey)}
                    />
                )}
            </Box>

            {/* List */}
            <Box
                sx={{
                    flex: 1,
                    position: 'relative',
                    padding: '5px 5px 30px 5px',
                    overflow: 'hidden',
                    display: 'flex', // ① 讓子元素依 flex 規則排版
                    flexDirection: 'column', // ② ScrollBarContainer 才吃得到 flex:1
                }}
            >
                <ScrollBarContainer>
                    <List sx={{ p: 0, userSelect: 'none' }}>
                        {safeOptions.map((item, idx) => {
                            const disabled =
                                !isFirstKey && (isEmptyAfterFilter || !filteredSet.has(item.id));
                            const isSelected = selected.includes(item.id);
                            return (
                                <ListItemButton
                                    key={item.id}
                                    data-testid={`${item.id}-filter-option`}
                                    selected={isSelected}
                                    disabled={disabled}
                                    onMouseDown={(e) => handleMouseDown(e, idx, item.id, disabled)}
                                    onMouseEnter={() => handleMouseEnter(idx)}
                                    sx={{
                                        p: 0,
                                        borderRadius: '4px',
                                        '&.Mui-selected, &.Mui-selected:hover': {
                                            backgroundColor: 'rgba(238,151,105,0.5)',
                                        },
                                        '&:hover': {
                                            backgroundColor: disabled ? 'inherit' : '#f7f7f7',
                                        },
                                        '&.Mui-disabled': { opacity: 0.5 },
                                    }}
                                >
                                    <ListItemText
                                        primary={
                                            getDisplayNameForParameterValues(item, language) || ''
                                        }
                                        primaryTypographyProps={{
                                            fontFamily: 'Poppins',
                                            fontWeight: 400,
                                            fontSize: '16px',
                                            lineHeight: '19px',
                                            color: '#191A1B',
                                        }}
                                    />
                                </ListItemButton>
                            );
                        })}
                    </List>
                </ScrollBarContainer>
            </Box>

            {/* Clear */}
            {selected.length > 0 && <ClearButton onClick={handleClear}>Clear</ClearButton>}
        </Box>
    );
};

export default FilterComponent;
